import { useEffect, useState } from "react";
import { responseHero } from "../../../../utils/axiosInterceptor";
import { toast } from "react-toastify";
import { Group } from "../../../../models/group.model";
import { Crypto } from "../../../../models/crypto.model";
import React from "react";
import { DepositRegisterSelector } from "../../../../models/types/DepositRegisterSelector";
import { getFreeCryptoDeposits } from "../../../../api/getHumanDCFunctions";

type ModalProps = {
    title: string;
    group: Group | undefined;
    showModal: boolean;
    closeModal: () => void;
    handleStateChange: () => void;
    row: Crypto | undefined;
    asset_id: number | undefined;
}

type ModifiedPayload = Pick<Crypto, 
"date" |
"operation" |
"note" |
"quantity" |
"exchange_rate" |
"amount" |
"creatorId" |
"group_id" |
"assetType" |
"percentage_to_alt" |
"percentage_to_gold" |
"percentage_to_mining" |
"robotFlag"> // Added robotFlag to type

const ModifyRecordModal = ({ group, title, showModal, closeModal, handleStateChange, asset_id, row } : ModalProps) => {

    const [operation, setOperation] = useState(true); // BUY
    const [isRobot, setIsRobot] = useState(false);
    const [depositRegistrations, setDepositRegistrations] = useState<DepositRegisterSelector[]>([])
    const [selectedDepositRegisterID, setSelectedDepositRegisterID] = useState(-1);

    const [recordData, setRecordData] = useState<ModifiedPayload>({
        date: new Date(),
        operation: true,
        quantity: "",
        exchange_rate: "",
        amount: 0,
        creatorId: -1,
        group_id: 0,
        note: "",
        assetType: 0,
        percentage_to_alt: 0,
        percentage_to_gold: 0,
        percentage_to_mining: 0,
        robotFlag: false, // Added initial state
    });

    // Detect if record is a Robot record on load
    useEffect(() => {
        if(!row) return;

        const isRobotNote = row.note === "ROBOT";
        setIsRobot(isRobotNote);
        setOperation(isRobotNote ? true : row.operation);

        setRecordData({
            date: row.date,
            operation: isRobotNote ? true : row.operation,
            quantity: row.quantity,
            exchange_rate: isRobotNote ? "0" : row.exchange_rate,
            amount: isRobotNote ? 0 : row.amount,
            creatorId: row.creatorId,
            group_id: group?.group_id || 0,
            note: row?.note || "",
            assetType: asset_id ? asset_id : -1,
            percentage_to_alt: row.percentage_to_alt,
            percentage_to_gold: row.percentage_to_gold,
            percentage_to_mining: row.percentage_to_mining,
            robotFlag: isRobotNote, // Set flag based on existing data
        });

        handleDepositSelection(-1)
    }, [row, group, asset_id]);

    React.useEffect(() => {
        if(!asset_id) return;
        getFreeCryptoDeposits(group, asset_id)
        .then((response) => {
            setDepositRegistrations(response)
        })
        .catch((error) => {
            console.error(error);
        }); 
    }, [asset_id, handleStateChange, group]);

    const handleRobotToggle = (checked: boolean) => {
        setIsRobot(checked);
        if (checked) {
            setOperation(true); 
            setRecordData((prev) => ({
                ...prev,
                operation: true,
                note: "ROBOT",
                exchange_rate: "0",
                amount: 0,
                robotFlag: true, // Set flag to true
            }));
        } else {
            setRecordData((prev) => ({ 
                ...prev, 
                note: "", 
                robotFlag: false // Revert flag
            }));
        }
    };

    const handleChange = (
        type: keyof ModifiedPayload,
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const inputValue = e.target.value;
    
        switch (type) {
            case "operation":
                setOperation((prev) => !prev);
                setRecordData((prevData) => ({
                    ...prevData,
                    [type]: !prevData.operation,
                }));
                break;
    
            case "exchange_rate":
                if (/^\d*\.?\d{0,4}$/.test(inputValue)) {
                    setRecordData((prevData) => ({ ...prevData, [type]: inputValue }));
                }
                break;
    
            case "quantity":
                if (/^\d*\.?\d{0,8}$/.test(inputValue)) {
                    setRecordData((prevData) => ({ ...prevData, [type]: inputValue }));
                }
                break;
    
            case "date":
            case "note":
                setRecordData((prevData) => ({ ...prevData, [type]: inputValue }));
                break;

            case "robotFlag":
                // Specifically handled by handleRobotToggle
                break;
    
            default:
                const parsedValue = inputValue === "" ? 0 : parseInt(inputValue, 10);
                setRecordData((prevData) => ({
                    ...prevData,
                    [type]: parsedValue,
                }));
                break;
        }
    };

    const updateRecord = async () => {
        if(!group || !row) return;

        if(selectedDepositRegisterID !== -1 && selectedDepositRegisterID > 0) {
            await responseHero.post(`${process.env.REACT_APP_API_URL}/humandc/cryptoDeposit`, { cryptoDeposit: {
                deposit_register_id: selectedDepositRegisterID,
                transaction_id: row?.transaction_id,
                group_id: group?.group_id,
                assetType: asset_id
            }}).catch(err => console.error(err));
        } else if (selectedDepositRegisterID === 0) {
             await responseHero.delete(`${process.env.REACT_APP_API_URL}/humandc/cryptoDeposit/${row?.transaction_id}`).catch(err => console.error(err));
        }

        // Sending recordData which now includes the robotFlag
        await responseHero.put(`${process.env.REACT_APP_API_URL}/humandc/crypto/${row.transaction_id}`, { record: recordData }).then(() => {
            closeModal()
            handleStateChange();
            toast.success(`Sikeresen módosítottál egy rekordot!`);
        }).catch(error => {
            toast.error(`Sikertelen adatrögzítés!`);
        })
    }

    const handleDepositSelection = (id: number) => setSelectedDepositRegisterID(id);

    return (
    <>
        {showModal && (
            <>
            <div className="flex justify-center items-center overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
                <div className="relative w-full my-6 mx-auto max-w-4xl">
                    <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                        <div className="flex items-start justify-between p-5 border-b border-solid border-gray-300 rounded-t">
                            <h3 className="text-xl text-brown font-semibold"><span className="text-dark-blue">HUMAN D.C</span> - {title}</h3>
                        </div>
                        <div className="relative p-6 flex-auto">
                        <form>
                            {/* ROBOT CHECKBOX */}
                            <div className="flex p-4 mb-4 bg-blue-50 rounded shadow-sm items-center justify-center border border-blue-200">
                                <label className="inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={isRobot}
                                        onChange={(e) => handleRobotToggle(e.target.checked)}
                                        className="form-checkbox h-5 w-5 text-dark-blue rounded"
                                    />
                                    <span className="ml-3 font-bold text-dark-blue uppercase tracking-wide">
                                        Ez egy ROBOT termelési adat
                                    </span>
                                </label>
                            </div>

                            {!isRobot && (
                                <div className="flex p-2 rounded shadow-md">
                                    <div className="w-full pr-2 text-center items-center">
                                        <label className="block text-sm font-bold mt-5 text-brown text-center justify-center">
                                            <span className="text-brown">Típus </span>
                                        </label>
                                        <div className="flex justify-center mt-2">
                                            <label className="inline-flex items-center">
                                                <input
                                                    type="radio"
                                                    checked={operation === true}
                                                    onChange={(e) => handleChange('operation', e)}
                                                    className="form-radio h-5 w-5 text-brown"
                                                />
                                                <span className="ml-2 text-success font-bold">BUY</span>
                                            </label>
                                            <label className="inline-flex items-center ml-6">
                                                <input
                                                    type="radio"
                                                    checked={operation === false}
                                                    onChange={(e) => handleChange('operation', e)}
                                                    className="form-radio h-5 w-5 text-brown"
                                                />
                                                <span className="ml-2 text-error font-bold">SELL</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="flex p-2 rounded shadow-md">
                                <div className="w-2/4 pr-2 text-center items-center">
                                    <label className="block text-sm font-bold mt-5 text-brown text-center justify-center">
                                        <span className="text-brown">Dátum </span>
                                    </label>
                                    <input
                                        value={recordData?.date?.toString().split('T')[0]}
                                        onChange={(e) => handleChange('date', e)}
                                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" type="date"/>
                                </div>

                                <div className="w-2/4 pr-2 text-center items-center">
                                    <label className="block text-sm font-bold mt-5 text-brown text-center justify-center">
                                        <span className="text-brown">Megjegyzés </span>
                                    </label>
                                    <input
                                        value={recordData?.note}
                                        disabled={isRobot}
                                        onChange={(e) => handleChange('note', e)}
                                        className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${isRobot ? 'bg-gray-100 italic' : ''}`}
                                        type="text"/>
                                </div>
                            </div>

                            <div className="flex p-2 rounded shadow-md">
                                {isRobot ? (
                                    <div className="w-full pr-2 text-center justify-center items-center">
                                        <label className="block text-sm font-bold mt-5 text-dark-blue text-center justify-center">
                                            <span className="uppercase">Termelt coinok mennyisége</span> <br/>(8 tizedesjegyig)
                                        </label>
                                        <input
                                            value={recordData?.quantity}
                                            onChange={(e) => handleChange('quantity', e)}
                                            className="shadow appearance-none border border-dark-blue rounded w-full py-2 px-3 text-gray-700 font-mono leading-tight focus:outline-none focus:shadow-outline text-center text-lg"
                                            type="number"
                                            step="0.00000001"
                                        />
                                    </div>
                                ) : (
                                    <>
                                        <div className="w-2/4 pr-2 text-center justify-center items-center">
                                            <label className="block text-sm font-bold mt-5 text-brown text-center justify-center">
                                                <span className="text-success">Árfolyam </span> <br/>USDT
                                            </label>
                                            <input
                                                value={recordData?.exchange_rate}
                                                onChange={(e) => handleChange('exchange_rate', e)}
                                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" type="number"/>
                                        </div>
                                        {operation ? (
                                            <div className="w-2/4 pr-2 text-center justify-center items-center">
                                                <label className="block text-sm font-bold mt-5 text-brown text-center justify-center">
                                                    <span className="text-success">Összeg </span> <br/>USDT
                                                </label>
                                                <input
                                                    value={recordData?.amount}
                                                    onChange={(e) => handleChange('amount', e)}
                                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" type="number"/>
                                            </div>
                                        ) : (
                                            <div className="w-2/4 pr-2 text-center justify-center items-center">
                                                <label className="block text-sm font-bold mt-5 text-brown text-center justify-center">
                                                    <span className="text-success">Kriptoeszköz </span> <br/>Darabszám
                                                </label>
                                                <input
                                                    value={recordData?.quantity}
                                                    onChange={(e) => handleChange('quantity', e)}
                                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" type="text"/>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                                {operation && !isRobot && (
                                    <div className="flex p-2 rounded shadow-md">
                                        <div className="w-full pr-2 text-center items-center">
                                    <label className="block text-sm font-bold mt-5 mb-2 text-brown text-center">
                                    <span className="text-success">Befizetés </span> <br/> A befizetés nyilvántartó egyik befizetése
                                    </label>

                                    <select defaultValue={-1} onChange={(e) => handleDepositSelection(parseInt(e.target.value))} className="w-full bg-dark-blue text-white px-3 py-2 rounded-lg shadow-md">
                                        <option value={-1} className="px-2 py-1 text-center">{"Csoporthoz (Nincs módosítás)"}</option>
                                        <option value={0} className="px-2 py-1 text-center text-red-300 italic font-bold">{"--- SZÉTKAPCSOLÁS (Befizetés törlése erről a sorról) ---"}</option>
                                        {depositRegistrations?.map((depositRegistration, index) => (
                                            <option value={depositRegistration.id} className="px-2 py-1 text-center" key={index}>[ID: {depositRegistration.id}] - {depositRegistration.user.name} ({depositRegistration.date.toString().split('T')[0]})</option>
                                        ))}
                                    </select>
                                    </div>
                                </div>
                                )}

                                {!operation && (
                                    <div className="flex p-2 rounded shadow-md">
                                        <div className="w-2/4 pr-2 text-center items-center">
                                            <label className="block text-sm font-bold mt-5 text-brown text-center justify-center">
                                                <span className="text-success">Százalék </span> <br/>Arany
                                            </label>
                                            <input
                                                value={recordData?.percentage_to_gold}
                                                onChange={(e) => handleChange('percentage_to_gold', e)}
                                                min="0" max="100" className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" type="number"/>
                                        </div>
                                        <div className="w-2/4 pr-2 text-center items-center">
                                            <label className="block text-sm font-bold mt-5 text-brown text-center justify-center">
                                                <span className="text-success">Százalék </span> <br/>Bányagép-energia
                                            </label>
                                            <input
                                                value={recordData?.percentage_to_mining}
                                                onChange={(e) => handleChange('percentage_to_mining', e)}
                                                min="0" max="100" className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" type="number"/>
                                        </div>
                                        <div className="w-2/4 pr-2 text-center items-center">
                                            <label className="block text-sm font-bold mt-5 text-brown text-center justify-center">
                                                <span className="text-success">Százalék </span> <br/>Altcoin
                                            </label>
                                            <input
                                                value={recordData?.percentage_to_alt}
                                                onChange={(e) => handleChange('percentage_to_alt', e)}
                                                min="0" max="100" className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" type="number"/>
                                        </div>
                                    </div>
                                )}
                            </form>
                        </div>
                        <div className="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b">
                        <button
                            className="text-red-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1"
                            type="button"
                            onClick={closeModal}
                        >
                            Mégse
                        </button>
                        <button
                            className="text-cream bg-dark-blue font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1"
                            type="button"
                            onClick={updateRecord}
                        >
                            Mentés
                        </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-filter backdrop-blur-sm"></div>
            </>
        )}
    </>
  );
};

export default ModifyRecordModal;